import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { Button } from "./ui/button";
import { SOLUTIONS } from "../data/content";

const Solutions = () => {
  return (
    <section id="solutions" className="relative py-24 sm:py-32 lg:py-40" data-testid="solutions-section">
      <div className="mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-12">
        <Reveal className="mb-16 max-w-3xl">
          <Eyebrow>01 — Our Solutions</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Flagship enterprise products, built to scale.
          </h2>
          <p className="mt-5 text-base text-muted-foreground sm:text-lg">
            Two intelligent platforms serving organisations across the UK and Nigeria — from care
            providers to multi-store retailers.
          </p>
        </Reveal>

        <div className="space-y-8">
          {SOLUTIONS.map((s, idx) => (
            <Reveal key={s.id} delay={idx * 0.08}>
              <div
                className="group grid grid-cols-1 gap-0 overflow-hidden rounded-3xl border border-border bg-card shadow-[0_8px_40px_rgb(0,0,0,0.05)] transition-shadow duration-500 hover:shadow-[0_20px_60px_rgb(13,148,136,0.12)] lg:grid-cols-2"
                data-testid={`solution-card-${s.id}`}
              >
                {/* Content */}
                <div className={`flex flex-col justify-center p-8 sm:p-12 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                  <span className="mb-4 inline-flex w-fit items-center rounded-full bg-teal/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-teal">
                    {s.subtitle}
                  </span>
                  <h3 className="font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                    {s.name === "AYOBELL" ? "AYOBELL Care" : s.name}
                  </h3>
                  <p className="mt-4 max-w-lg text-muted-foreground">{s.description}</p>

                  <div className="mt-7 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                    {s.features.map((f) => (
                      <div key={f} className="flex items-center gap-2.5 text-sm text-foreground">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal/15">
                          <Check className="h-3 w-3 text-teal" strokeWidth={3} />
                        </span>
                        {f}
                      </div>
                    ))}
                  </div>

                  <a href="#contact" className="mt-9">
                    <Button
                      data-testid={`learn-more-${s.id}`}
                      className="group/btn h-11 rounded-full bg-navy px-6 font-semibold text-white hover:bg-navy/90"
                    >
                      Learn More
                      <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                    </Button>
                  </a>
                </div>

                {/* Image */}
                <div className={`relative flex items-center justify-center overflow-hidden bg-secondary/50 p-4 sm:p-5 lg:p-6 ${idx % 2 === 1 ? "lg:order-1" : ""}`}>
                  <div className="pointer-events-none absolute inset-0 grid-bg opacity-40" />
                  <motion.img
                    src={s.image}
                    alt={`${s.name} dashboard`}
                    loading="lazy"
                    className="relative z-10 w-full rounded-xl border border-border object-contain shadow-2xl"
                    whileHover={{ scale: 1.03, rotate: -0.5 }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Solutions;
